import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { APIPlayground } from '@/components/APIPlayground';
import { WebhookDeliveryLogs } from '@/components/WebhookDeliveryLogs';
import { 
  Code, 
  Key, 
  Copy, 
  Eye, 
  EyeOff, 
  Plus,
  Trash2,
  Activity,
  BookOpen,
  Terminal,
  Zap,
  Shield,
  BarChart3,
  Loader2,
  Play
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  status: string;
  created_at: string;
  last_used_at: string | null;
}

interface Webhook {
  id: string;
  url: string;
  events: string[];
  is_active: boolean;
  created_at: string;
}

const generateApiKey = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let key = 'wf_live_sk_';
  for (let i = 0; i < 32; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
};

const hashKey = async (key: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const API = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showApiKeys, setShowApiKeys] = useState<{ [key: string]: string }>({});
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>(['form.submitted']);
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [apiStats, setApiStats] = useState({
    callsToday: 0,
    successRate: 100,
    avgResponseTime: 0,
    monthlyQuota: { used: 0, total: 10000 }
  });

  useEffect(() => {
    if (user) {
      fetchApiKeys();
      fetchWebhooks();
      fetchApiStats();
    }
  }, [user]);

  const fetchApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWebhooks(data || []);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    }
  };

  const fetchApiStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: logs, error } = await supabase
        .from('api_usage_logs')
        .select('*')
        .eq('user_id', user?.id)
        .gte('created_at', today.toISOString());

      if (error) throw error;

      const callsToday = logs?.length || 0;
      const successfulCalls = logs?.filter(l => l.status_code && l.status_code < 400).length || 0;
      const successRate = callsToday > 0 ? (successfulCalls / callsToday) * 100 : 100;
      const avgResponseTime = callsToday > 0
        ? logs?.reduce((sum, l) => sum + (l.response_time_ms || 0), 0) / callsToday
        : 0;

      // Get monthly usage
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const { count } = await supabase
        .from('api_usage_logs')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user?.id)
        .gte('created_at', monthStart.toISOString());

      setApiStats({
        callsToday,
        successRate,
        avgResponseTime: Math.round(avgResponseTime),
        monthlyQuota: { used: count || 0, total: 10000 }
      });
    } catch (error) {
      console.error('Error fetching API stats:', error);
    }
  };

  const toggleApiKeyVisibility = (keyId: string, fullKey?: string) => {
    setShowApiKeys(prev => {
      if (prev[keyId]) {
        const newState = { ...prev };
        delete newState[keyId];
        return newState;
      }
      return { ...prev, [keyId]: fullKey || '' };
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "API key has been copied to your clipboard",
    });
  };

  const createApiKey = async () => {
    if (!newKeyName.trim() || !user) return;
    
    setIsCreatingKey(true);
    try {
      const fullKey = generateApiKey();
      const keyHash = await hashKey(fullKey);
      const keyPrefix = fullKey.substring(0, 15) + '...';

      const { error } = await supabase.from('api_keys').insert({
        user_id: user.id,
        name: newKeyName,
        key_prefix: keyPrefix,
        key_hash: keyHash,
        permissions: ['read', 'write'],
        status: 'active'
      });

      if (error) throw error;

      toast({
        title: "API Key Created",
        description: "Copy your new API key now. You won't be able to see it again!",
      });

      // Show the full key temporarily
      fetchApiKeys();
      setNewKeyName('');
      
      // Store the full key temporarily for display
      setTimeout(() => {
        setShowApiKeys(prev => ({ ...prev, 'new': fullKey }));
      }, 500);

      // Copy to clipboard automatically
      copyToClipboard(fullKey);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingKey(false);
    }
  };

  const deleteApiKey = async (keyId: string, keyName: string) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', keyId);

      if (error) throw error;

      toast({
        title: "API Key Deleted",
        description: `API key "${keyName}" has been deleted`,
      });
      
      fetchApiKeys();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveWebhook = async () => {
    if (!webhookUrl.trim() || !user) return;

    setIsSavingWebhook(true);
    try {
      const { error } = await supabase.from('webhooks').insert({
        user_id: user.id,
        url: webhookUrl,
        events: webhookEvents,
        is_active: true
      });

      if (error) throw error;

      toast({
        title: "Webhook Saved",
        description: "Your webhook has been configured",
      });

      setWebhookUrl('');
      setWebhookEvents(['form.submitted']);
      fetchWebhooks();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const deleteWebhook = async (webhookId: string) => {
    try {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', webhookId);

      if (error) throw error;

      toast({
        title: "Webhook Deleted",
        description: "Webhook has been removed",
      });
      
      fetchWebhooks();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const toggleWebhookEvent = (event: string) => {
    setWebhookEvents(prev => 
      prev.includes(event) 
        ? prev.filter(e => e !== event)
        : [...prev, event]
    );
  };

  const codeExamples = {
    javascript: `// Initialize Bravax Forms API
const API_KEY = 'your_api_key_here';
const BASE_URL = '${window.location.origin}/api/v1';

// Get all forms
const response = await fetch(\`\${BASE_URL}/forms\`, {
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  }
});

const forms = await response.json();
console.log('Forms:', forms);

// Get form submissions
const submissions = await fetch(\`\${BASE_URL}/forms/{formId}/submissions\`, {
  headers: { 'Authorization': \`Bearer \${API_KEY}\` }
});`,
    
    python: `# Bravax Forms Python SDK
import requests

API_KEY = 'your_api_key_here'
BASE_URL = '${window.location.origin}/api/v1'

headers = {
    'Authorization': f'Bearer {API_KEY}',
    'Content-Type': 'application/json'
}

# Get all forms
response = requests.get(f'{BASE_URL}/forms', headers=headers)
forms = response.json()
print(f'Forms: {forms}')

# Get form submissions
submissions = requests.get(
    f'{BASE_URL}/forms/{form_id}/submissions',
    headers=headers
)`,

    curl: `# Get all forms
curl -X GET "${window.location.origin}/api/v1/forms" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"

# Get form submissions
curl -X GET "${window.location.origin}/api/v1/forms/{formId}/submissions" \\
  -H "Authorization: Bearer your_api_key_here"

# Create a submission (webhook test)
curl -X POST "${window.location.origin}/api/v1/forms/{formId}/submissions" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "John Doe", "email": "john@example.com"}'`
  };

  const isPaidPlan = user && ['pro', 'business', 'enterprise'].includes(user.plan?.toLowerCase() || '');

  if (!isPaidPlan) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>API Access | Bravax Forms</title>
        </Helmet>
        <div className="p-6">
          <Card className="card-elegant">
            <CardContent className="p-12 text-center">
              <Code className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">API Access</h2>
              <p className="text-muted-foreground mb-6">
                Upgrade to Pro plan to access our powerful REST API and integrate Bravax Forms with your applications.
              </p>
              <Button onClick={() => window.location.href = '/billing-new'}>
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const statsDisplay = [
    {
      title: 'API Calls Today',
      value: apiStats.callsToday.toString(),
      change: 'Today',
      icon: Activity
    },
    {
      title: 'Success Rate',
      value: `${apiStats.successRate.toFixed(1)}%`,
      change: 'All time',
      icon: Shield
    },
    {
      title: 'Avg Response Time',
      value: `${apiStats.avgResponseTime}ms`,
      change: 'Today',
      icon: Zap
    },
    {
      title: 'Monthly Quota',
      value: `${(apiStats.monthlyQuota.used / 1000).toFixed(1)}k / ${apiStats.monthlyQuota.total / 1000}k`,
      change: `${Math.round((apiStats.monthlyQuota.used / apiStats.monthlyQuota.total) * 100)}%`,
      icon: BarChart3
    }
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>API Access | Bravax Forms</title>
      </Helmet>
      <div className="p-6 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">API Access</h1>
          <p className="text-muted-foreground">
            Integrate Bravax Forms with your applications using our REST API
          </p>
        </div>

        {/* API Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsDisplay.map((stat, index) => (
            <Card key={index} className="card-elegant">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-xs font-medium text-muted-foreground">{stat.change}</p>
                  </div>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* API Management */}
        <Tabs defaultValue="keys" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="keys">API Keys</TabsTrigger>
            <TabsTrigger value="playground">Playground</TabsTrigger>
            <TabsTrigger value="docs">Documentation</TabsTrigger>
            <TabsTrigger value="examples">Code Examples</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="logs">Delivery Logs</TabsTrigger>
          </TabsList>

          <TabsContent value="keys" className="space-y-6">
            {/* Create New API Key */}
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Plus className="h-5 w-5 text-primary" />
                  <span>Create New API Key</span>
                </CardTitle>
                <CardDescription>
                  Generate a new API key for your applications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex space-x-4">
                  <Input
                    placeholder="Enter key name (e.g., Production, Development)"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={createApiKey} disabled={!newKeyName.trim() || isCreatingKey}>
                    {isCreatingKey ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Create Key
                  </Button>
                </div>

                {showApiKeys['new'] && (
                  <div className="p-4 bg-muted rounded-lg border border-primary">
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Important:</strong> Copy your API key now. You won't be able to see it again!
                    </p>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 font-mono text-sm bg-background p-2 rounded">
                        {showApiKeys['new']}
                      </code>
                      <Button size="sm" onClick={() => copyToClipboard(showApiKeys['new'])}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Existing API Keys */}
            <div className="space-y-4">
              {apiKeys.length === 0 ? (
                <Card className="card-elegant">
                  <CardContent className="p-12 text-center">
                    <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No API Keys</h3>
                    <p className="text-muted-foreground">
                      Create your first API key to start integrating with Bravax Forms.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                apiKeys.map((apiKey) => (
                  <Card key={apiKey.id} className="card-elegant">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-3">
                            <h3 className="font-semibold text-foreground">{apiKey.name}</h3>
                            <Badge variant={apiKey.status === 'active' ? 'default' : 'secondary'}>
                              {apiKey.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <span>Created: {new Date(apiKey.created_at).toLocaleDateString()}</span>
                            <span>Last used: {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleDateString() : 'Never'}</span>
                            <div className="flex space-x-1">
                              {apiKey.permissions.map((permission) => (
                                <Badge key={permission} variant="outline" className="text-xs">
                                  {permission}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <div className="flex-1 font-mono text-sm bg-muted p-2 rounded border">
                              {apiKey.key_prefix}
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteApiKey(apiKey.id, apiKey.name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="playground">
            <APIPlayground apiKeys={apiKeys} />
          </TabsContent>

          <TabsContent value="docs">
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <span>API Documentation</span>
                </CardTitle>
                <CardDescription>
                  Complete API reference, endpoints, and integration guides
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center py-8 space-y-4">
                  <BookOpen className="h-16 w-16 text-primary mx-auto" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Comprehensive API Documentation</h3>
                    <p className="text-muted-foreground mb-4">
                      Access detailed guides for REST API, iFrame embedding, webhooks, and more
                    </p>
                    <Button className="btn-hero" onClick={() => window.location.href = '/api-docs'}>
                      <BookOpen className="mr-2 h-4 w-4" />
                      View Full Documentation
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="examples">
            <div className="space-y-6">
              <Tabs defaultValue="javascript">
                <TabsList>
                  <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                </TabsList>
                
                {Object.entries(codeExamples).map(([lang, code]) => (
                  <TabsContent key={lang} value={lang}>
                    <Card className="card-elegant">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center space-x-2">
                          <Terminal className="h-5 w-5 text-primary" />
                          <span>{(lang?.charAt(0) || "").toUpperCase() + (lang?.slice(1) || "")} Example</span>
                        </CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(code)}
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copy
                        </Button>
                      </CardHeader>
                      <CardContent>
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto">
                          <code className="text-sm">{code}</code>
                        </pre>
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </TabsContent>

          <TabsContent value="webhooks">
            <div className="space-y-6">
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Zap className="h-5 w-5 text-primary" />
                    <span>Add Webhook</span>
                  </CardTitle>
                  <CardDescription>
                    Receive real-time notifications when events occur
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Webhook URL</Label>
                    <Input 
                      placeholder="https://yourapp.com/webhook" 
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      className="mt-1" 
                    />
                  </div>
                  
                  <div>
                    <Label>Events</Label>
                    <div className="mt-2 space-y-2">
                      {['form.submitted', 'form.created', 'form.updated', 'form.deleted'].map(event => (
                        <div key={event} className="flex items-center space-x-2">
                          <Checkbox
                            id={event}
                            checked={webhookEvents.includes(event)}
                            onCheckedChange={() => toggleWebhookEvent(event)}
                          />
                          <Label htmlFor={event} className="text-sm font-normal cursor-pointer">
                            {event}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Button 
                    className="btn-hero" 
                    onClick={saveWebhook}
                    disabled={!webhookUrl.trim() || isSavingWebhook}
                  >
                    {isSavingWebhook ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Webhook
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Webhooks */}
              {webhooks.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Configured Webhooks</h3>
                  {webhooks.map((webhook) => (
                    <Card key={webhook.id} className="card-elegant">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="font-mono text-sm">{webhook.url}</p>
                            <div className="flex items-center gap-2">
                              {webhook.events.map(event => (
                                <Badge key={event} variant="secondary" className="text-xs">
                                  {event}
                                </Badge>
                              ))}
                              <Badge variant={webhook.is_active ? 'default' : 'outline'}>
                                {webhook.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteWebhook(webhook.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <WebhookDeliveryLogs />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default API;
